// Task-Run-Historie fuer /infra/runs. RunRecord[] + retention_days
// kommen von /v2-api/runs?hours=N|from_ms=&to_ms=. Polling 10 s; Runs
// werden per WS-Channel "runs:<id>" sub-sekunden gepusht, aber nur
// pro selektierter Run, nicht fuer die Liste -- daher Polling als
// Failsafe + manuelles refresh nach Mutationen.
//
// SWR: ja -- Reload paint sofort die letzten Runs aus localStorage.
//
// TimeRange ist parametrisierbar: User waehlt im TimeRangePicker auf
// der Page; Default 24 h. Range-Wechsel triggert sofortigen Refetch
// via setRunsRange().

import { SvelteMap } from "svelte/reactivity";
import { registerRevalidator } from "./revalidate.svelte";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { rangeFromPreset, type TimeRange } from "./TimeRangePicker.svelte";
import type { RunRecord } from "./types";

export interface RunsSummary {
  runs: RunRecord[];
  /** So lange bleiben erfolgreiche Läufe ... */
  retention_days: number;
  /** ... und so lange gescheiterte und abgebrochene. */
  failed_retention_days?: number;
}

const STORAGE_KEY = "runs";

const _state = new SvelteMap<string, RunsSummary>();
let _started = false;
let _timer: ReturnType<typeof setInterval> | undefined;
// Wieviele Ansichten die Liste GERADE zeigen. Der Poll haengt daran und
// nicht am App-Start: die Liste ist die groesste Antwort im Haus, und
// sie alle 10 s zu holen, waehrend niemand hinsieht, war reine Last
// (gemessen 16.08.: 4,8 MB je Abruf, auf jeder Seite).
let _beobachter = 0;
let _range: TimeRange = rangeFromPreset(24);

function rangeQuery(): string {
  if (_range.preset_hours != null) {
    return `?hours=${_range.preset_hours}`;
  }
  return `?from_ms=${_range.from_ms}&to_ms=${_range.to_ms}`;
}

// Wieviele Laeufe der localStorage-Snapshot traegt. Der Snapshot ist
// fuer das erste Bild nach dem Reload da, nicht als zweite Datenbank:
// die volle 24-h-Liste waren 1,5 MB, die der Store alle 10 s neu
// stringifyte und schrieb -- Arbeit im Main-Thread, und der Platz fehlte
// dem Embedding-Cache in derselben Quota (siehe semantic-search).
const PERSIST_MAX = 300;

function persistSlim(data: RunsSummary): void {
  persist(STORAGE_KEY, {
    ...data,
    runs: data.runs.length > PERSIST_MAX ? data.runs.slice(0, PERSIST_MAX) : data.runs,
  });
}

async function refresh(): Promise<void> {
  if (_beobachter === 0) return;
  try {
    const r = await fetch(`/v2-api/runs${rangeQuery()}`, {
      credentials: "include",
    });
    if (!r.ok) return;
    const data = (await r.json()) as RunsSummary;
    _state.set("current", data);
    persistSlim(data);
  } catch {
    /* ignore */
  }
}

// --- Ausschnitt fuer das untere Panel -------------------------------
//
// Das Panel zeigt 40 Zeilen. Es dafuer an der vollen Liste haengen zu
// lassen hiess, fuer 40 sichtbare Zeilen ~9000 Laeufe zu holen. Der
// Ausschnitt holt die neuesten 60 plus alles, was gerade laeuft
// (with_running=1) -- sonst fiele ein seit Stunden laufender Scrub aus
// den "neuesten 60" heraus, und genau der gehoert oben hin.
const PANEL_LIMIT = 60;
const PANEL_KEY = "runs-panel";

const _panel = new SvelteMap<string, RunsSummary>();
let _panelBeobachter = 0;
let _panelTimer: ReturnType<typeof setInterval> | undefined;

async function refreshPanel(): Promise<void> {
  if (_panelBeobachter === 0) return;
  try {
    const r = await fetch(`/v2-api/runs?limit=${PANEL_LIMIT}&with_running=1`, {
      credentials: "include",
    });
    if (!r.ok) return;
    const data = (await r.json()) as RunsSummary;
    _panel.set("current", data);
    persist(PANEL_KEY, data);
  } catch {
    /* ignore */
  }
}

let _panelHydriert = false;

// Den SWR-Schnappschuss genau einmal einspielen.
//
// Das MUSS an einem einfachen Schalter haengen und nicht an
// `_panel.has(...)`: beobachtePanelLaeufe() laeuft in einem $effect, und
// eine Abfrage auf die SvelteMap ist dort ein reaktives LESEN. Zusammen
// mit dem set() daneben weckt der Effekt sich selbst -- gemessen am
// 23.08.2026 waren das 306 Abrufe in 14 Sekunden, also genau die
// Anfragen-Flut, die dieser Umbau abstellen sollte
// (docs/lessons: "$effect schreibt was er liest").
function hydratisierePanel(): void {
  if (_panelHydriert) return;
  _panelHydriert = true;
  const cached = loadPersisted<RunsSummary>(PANEL_KEY);
  if (cached) _panel.set("current", cached);
}

// Anmelden, solange das Panel den Laeufe-Reiter zeigt.
export function beobachtePanelLaeufe(): () => void {
  hydratisierePanel();
  _panelBeobachter++;
  if (_panelBeobachter === 1) {
    void refreshPanel();
    _panelTimer = setInterval(refreshPanel, 10_000);
  }
  return () => {
    _panelBeobachter--;
    if (_panelBeobachter === 0 && _panelTimer) {
      clearInterval(_panelTimer);
      _panelTimer = undefined;
    }
  };
}

export function getPanelLaeufe(): RunsSummary | undefined {
  return _panel.get("current");
}

export function startRunsSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  const cached = loadPersisted<RunsSummary>(STORAGE_KEY);
  if (cached) _state.set("current", cached);
}

// Anmelden, solange eine Ansicht die Liste zeigt; der Rueckgabewert
// meldet wieder ab. Der erste Beobachter startet den Poll, der letzte
// beendet ihn -- in Svelte reicht dafuer $effect(() => beobachteRuns()).
export function beobachteRuns(): () => void {
  startRunsSummary();
  _beobachter++;
  if (_beobachter === 1) {
    void refresh();
    _timer = setInterval(refresh, 10_000);
  }
  return () => {
    _beobachter--;
    if (_beobachter === 0 && _timer) {
      clearInterval(_timer);
      _timer = undefined;
    }
  };
}

export function stopRunsSummary(): void {
  _started = false;
  _beobachter = 0;
  if (_timer) {
    clearInterval(_timer);
    _timer = undefined;
  }
  _state.clear();
}

export function getRunsSummary(): RunsSummary | undefined {
  return _state.get("current");
}

// Range aendern + sofort refetchen. User-Aktion auf der Page
// (TimeRangePicker); naechster regulaerer Tick laeuft danach mit dem
// neuen Range weiter.
export function setRunsRange(r: TimeRange): void {
  if (_range.from_ms === r.from_ms && _range.to_ms === r.to_ms) return;
  _range = r;
  void refresh();
}

export function refreshRunsSummary(): Promise<void> {
  return refresh();
}
